import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Check } from "lucide-react";
import Picker from "react-mobile-picker";
import "./DogPersonalityPage.css";

import progressIcon1 from "../../assets/progressIcon4.png";
import progressIcon2 from "../../assets/progressIcon3.png";
import option1Icon from "../../assets/girlAndDog1.png";
import option2Icon from "../../assets/girlAndDog2.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";

import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";

const DogPersonalityPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const profile = useSelector((state) => state.user.profile);
  const isSocialSignup = profile?.is_social_signup === 1;

  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  // LEFT PANEL LOGIC (Experience Selection)
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isExperienceSelected =
    typeof formData.dogActivity === "string" &&
    formData.dogActivity.trim().length > 0;

  // RIGHT PANEL LOGIC (Birthday & Age Group)
  const today = useMemo(() => new Date(), []);
  const initialDate = useMemo(() => {
    return formData.dogBirthDate ? new Date(formData.dogBirthDate) : today;
  }, [formData.dogBirthDate, today]);

  const [activeSection, setActiveSection] = useState(
    formData.dogBirthDate
      ? "birthday"
      : formData.dogAgeGroup
        ? "ageGroup"
        : null,
  );

  const [bYear, setBYear] = useState(initialDate.getFullYear());
  const [bMonth, setBMonth] = useState(initialDate.getMonth());
  const [bDay, setBDay] = useState(initialDate.getDate());

  const lastWheelValues = useRef({ y: null, m: null, d: null });

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear + 1;
    const endYear = currentYear - 25;
    const years = [];
    for (let y = startYear; y >= endYear; y--) {
      years.push({ label: y.toString(), value: y });
    }
    return years;
  }, []);

  const monthOptions = useMemo(() => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months.map((m, i) => ({ label: m, value: i }));
  }, []);

  const dayOptions = useMemo(() => {
    const daysInMonth = new Date(bYear, bMonth + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => ({
      label: (i + 1).toString(),
      value: i + 1,
    }));
  }, [bYear, bMonth]);

  const updateBirthdayData = (y, m, d) => {
    const maxDays = new Date(y, m + 1, 0).getDate();
    const safeDay = Math.min(d, maxDays);
    const formattedDate = `${y}-${String(m + 1).padStart(2, "0")}-${String(safeDay).padStart(2, "0")}`;

    setFormData((prev) => ({
      ...prev,
      dogBirthDate: formattedDate,
      dogAgeGroup: "",
    }));
  };

  useEffect(() => {
    if (lastWheelValues.current.y === null) {
      lastWheelValues.current = { y: bYear, m: bMonth, d: bDay };
      return;
    }

    if (
      lastWheelValues.current.y !== bYear ||
      lastWheelValues.current.m !== bMonth ||
      lastWheelValues.current.d !== bDay
    ) {
      setActiveSection("birthday");
      updateBirthdayData(bYear, bMonth, bDay);
      lastWheelValues.current = { y: bYear, m: bMonth, d: bDay };
    }
  }, [bYear, bMonth, bDay]);

  const handleAgeGroupSelect = (label) => {
    setActiveSection("ageGroup");
    setFormData((prev) => ({
      ...prev,
      dogAgeGroup: label,
      dogBirthDate: "",
    }));
  };

  const ageText = useMemo(() => {
    const birthDate = new Date(bYear, bMonth, bDay);
    const now = new Date();
    if (birthDate > now) return "0 YEARS OLD";
    let years = now.getFullYear() - birthDate.getFullYear();
    let months = now.getMonth() - birthDate.getMonth();
    let days = now.getDate() - birthDate.getDate();
    if (days < 0) months -= 1;
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    if (years <= 0) {
      const totalMonths = Math.max(0, months + (days >= 0 ? 0 : -1)) || 0;
      return `${totalMonths} MONTH${totalMonths === 1 ? "" : "S"} OLD`;
    }
    return `${years} YEAR${years === 1 ? "" : "S"} OLD`;
  }, [bYear, bMonth, bDay]);

  const [pickerValue, setPickerValue] = useState({
    month: bMonth,
    day: bDay,
    year: bYear,
  });

  const handlePickerChange = (newValue) => {
    setPickerValue(newValue);
    setBMonth(newValue.month);
    setBDay(newValue.day);
    setBYear(newValue.year);
  };

  const monthOptionsRef = useRef(monthOptions);
  const dayOptionsRef = useRef(dayOptions);
  const yearOptionsRef = useRef(yearOptions);

  useEffect(() => {
    monthOptionsRef.current = monthOptions;
    dayOptionsRef.current = dayOptions;
    yearOptionsRef.current = yearOptions;
  }, [monthOptions, dayOptions, yearOptions]);

  const pickerContainerRef = useRef(null);

  useEffect(() => {
    const container = pickerContainerRef.current;
    if (!container) return;

    let isDragging = false;
    let startY = 0;
    let draggingCol = null;
    let wheelAccumulator = 0;
    let dragAccumulator = 0;
    let lastMoveTime = 0;

    const WHEEL_SENSITIVITY = 1;
    const DRAG_SENSITIVITY = 1;
    const WHEEL_THRESHOLD = 30;
    const DRAG_THRESHOLD = 25;
    const MOVE_COOLDOWN = 60;

    const canMove = () => {
      const now = Date.now();
      if (now - lastMoveTime < MOVE_COOLDOWN) return false;
      lastMoveTime = now;
      return true;
    };

    const movePicker = (col, step) => {
      setPickerValue((prev) => {
        let options = [];
        if (col === "month") options = monthOptionsRef.current;
        if (col === "day") options = dayOptionsRef.current;
        if (col === "year") options = yearOptionsRef.current;

        const currentIndex = options.findIndex((o) => o.value === prev[col]);
        let nextIndex = currentIndex + step;

        if (nextIndex < 0) nextIndex = 0;
        if (nextIndex >= options.length) nextIndex = options.length - 1;

        const newValue = options[nextIndex].value;

        if (col === "month") setBMonth(newValue);
        if (col === "day") setBDay(newValue);
        if (col === "year") setBYear(newValue);

        return { ...prev, [col]: newValue };
      });
    };

    const getColumnFromX = (clientX) => {
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left;
      const third = rect.width / 3;
      if (x < third) return "month";
      if (x < third * 2) return "day";
      return "year";
    };

    const handleWheel = (e) => {
      e.preventDefault();
      const col = getColumnFromX(e.clientX);
      wheelAccumulator += e.deltaY * WHEEL_SENSITIVITY;
      if (Math.abs(wheelAccumulator) >= WHEEL_THRESHOLD && canMove()) {
        const direction = wheelAccumulator > 0 ? 1 : -1;
        movePicker(col, direction);
        wheelAccumulator = 0;
      }
    };

    const handleInteractionStart = (e) => {
      isDragging = true;
      dragAccumulator = 0;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      draggingCol = getColumnFromX(clientX);
      startY = clientY;
    };

    const handleInteractionMove = (e) => {
      if (!isDragging || !draggingCol) return;
      if (e.cancelable) e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const deltaY = clientY - startY;
      dragAccumulator += deltaY * DRAG_SENSITIVITY;

      if (Math.abs(dragAccumulator) >= DRAG_THRESHOLD && canMove()) {
        const direction = dragAccumulator > 0 ? -1 : 1;
        movePicker(draggingCol, direction);
        dragAccumulator = 0;
        startY = clientY;
      }
    };

    const handleInteractionEnd = () => {
      isDragging = false;
      draggingCol = null;
      dragAccumulator = 0;
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("mousedown", handleInteractionStart, {
      passive: false,
    });
    window.addEventListener("mousemove", handleInteractionMove, {
      passive: false,
    });
    window.addEventListener("mouseup", handleInteractionEnd);
    container.addEventListener("touchstart", handleInteractionStart, {
      passive: false,
    });
    window.addEventListener("touchmove", handleInteractionMove, {
      passive: false,
    });
    window.addEventListener("touchend", handleInteractionEnd);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("mousedown", handleInteractionStart);
      window.removeEventListener("mousemove", handleInteractionMove);
      window.removeEventListener("mouseup", handleInteractionEnd);
      container.removeEventListener("touchstart", handleInteractionStart);
      window.removeEventListener("touchmove", handleInteractionMove);
      window.removeEventListener("touchend", handleInteractionEnd);
    };
  }, []);

  // VALIDATION AND NAVIGATION
  const isRightPanelValid =
    (activeSection === "birthday" && formData.dogBirthDate) ||
    (activeSection === "ageGroup" && formData.dogAgeGroup);

  const isNextEnabled = Boolean(isExperienceSelected && isRightPanelValid);

  const handleDesktopNext = () => {
    navigate("/team-paws-personality");
  };

  const handleBackToDetails = () => {
    const isAddingAnotherDog =
      location.state?.from === "add-another-dog" || formData.isAddingAnotherDog;

    // Route backward in the secondary dog loop
    if (isAddingAnotherDog) {
      navigate("/dog-details", { state: { step: 2, from: "add-another-dog" } });
      return;
    }

    switch (formData.dogPersonalityOrigin) {
      case "half-onboarding":
        navigate("/half-onboarding-page");
        break;
      case "half-completed":
        navigate("/half-completed-page");
        break;
      default:
        navigate(-1);
    }
  };

  const inactiveStyle = {
    opacity: 0.3,
    filter: "grayscale(1)",
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const activeStyle = {
    opacity: 1,
    filter: "grayscale(0)",
    transition: "all 0.3s ease",
  };

  return (
    <div
      className={`dp-container ${mobileStep === 1 ? "dp-mobile-step-1" : "dp-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Experience Selection (Mobile Step 1) */}
      <div
        className={`dp-panel dp-left-panel ${mobileStep === 1 ? "dp-mobile-active" : "dp-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(2)}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={handleBackToDetails}
          backColor="#000"
          showProgress={true}
          progressIconSrc={progressIcon1}
          progressPercent={40}
          iconLeft={33}
          iconWidth="80px"
        />

        <div className="dp-content-wrapper">
          <div className="dp-header-section mobile-only">
            <div className="dp-dog-icon-container">
              <img
                src={progressIcon1}
                alt="Progress Step"
                className="dp-progress-icon"
                style={
                  window.innerWidth <= 1023
                    ? { height: "65px", marginLeft: "-1rem" }
                    : {}
                }
              />
            </div>
            <div className="dp-progress-track">
              <div className="dp-progress-fill dp-step-1-fill"></div>
            </div>
          </div>

          <h1 className="dp-title-text">
            WHAT EXPERIENCE IS{" "}
            {formData.dogName ? formData.dogName.toUpperCase() : "[DOG NAME]"}{" "}
            WELL-SUITED FOR?
          </h1>

          <div className="dp-options-container">
            {/* Option 1: Exercise */}
            <div className="dp-option-wrapper">
              <div
                className={`dp-option-card ${
                  formData.dogActivity === "exercise" ? "selected" : ""
                }`}
                onClick={() => updateField("dogActivity", "exercise")}
              >
                <div className="dp-exercise-img">
                  <img
                    src={option1Icon}
                    alt="Girl Playing"
                    className="dp-img-contain"
                  />
                </div>
                <div className="dp-card-content">
                  <h3>EXERCISE</h3>
                </div>
              </div>
              <p className="dp-option-desc">
                For active pups who make awesome walking, jogging, or hiking
                companions.
              </p>
            </div>

            {/* Option 2: Companionship */}
            <div className="dp-option-wrapper">
              <div
                className={`dp-option-card ${
                  formData.dogActivity === "companionship" ? "selected" : ""
                }`}
                onClick={() => updateField("dogActivity", "companionship")}
              >
                <div className="dp-card-content">
                  <h3>COMPANIONSHIP</h3>
                </div>
                <div className="dp-companion-img">
                  <img
                    src={option2Icon}
                    alt="Girl Walking"
                    className="dp-img-contain"
                  />
                </div>
              </div>
              <p className="dp-option-desc">
                For calmer pups who make perfect furry co-workers, reading
                buddies, or movie sidekicks.
              </p>
            </div>

            {/* Option 3: Both */}
            <div className="dp-option-wrapper">
              <div
                className={`dp-option-card dp-full-width ${
                  formData.dogActivity === "both" ? "selected-blue" : ""
                }`}
                onClick={() => updateField("dogActivity", "both")}
              >
                <div className="dp-btn-content">
                  {formData.dogActivity === "both" && (
                    <Check
                      size={24}
                      strokeWidth={3}
                      color="white"
                      className="dp-desktop-check"
                    />
                  )}
                  {formData.dogActivity === "both" && (
                    <Check
                      size={20}
                      strokeWidth={3}
                      color="white"
                      className="dp-mobile-check"
                    />
                  )}
                  <span
                    className={`dp-btn-text ${
                      formData.dogActivity === "both" ? "white-text" : ""
                    }`}
                  >
                    BOTH
                  </span>
                </div>
              </div>
              <p className="dp-option-desc">
                Best of both worlds: playful pups who love to move, and enjoy
                winding down for snuggles.
              </p>
            </div>
          </div>

          <MobileBottomBar
            onNext={() =>
              navigate("/team-paws-personality", { state: { returnToStep: 2 } })
            }
            isNextEnabled={isExperienceSelected}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Age Group/Birthday Selection (Mobile Step 2) */}
      <div
        className={`dp-panel dp-right-panel ${mobileStep === 2 ? "dp-mobile-active" : "dp-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleBackToDetails}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div className="dp-content-wrapper dp-prompts-wrapper">
          <div className="dp-header-section dp-mobile-only">
            <div className="dp-dog-icon-container">
              <img
                src={progressIcon2}
                alt="Progress Step"
                className="dp-progress-icon"
                style={{ height: "65px", marginLeft: "-2rem" }}
              />
            </div>
            <div className="dp-progress-track">
              <div className="dp-progress-fill dp-step-2-fill"></div>
            </div>
          </div>

          {/* SECTION 1: BIRTHDAY */}
          <div
            className="dp-selection-row"
            style={activeSection === "ageGroup" ? inactiveStyle : activeStyle}
            onClick={() =>
              activeSection === "ageGroup" && setActiveSection("birthday")
            }
          >
            <div className="dp-content-column" style={{ width: "100%" }}>
              <h2 className="dp-section-title">BIRTHDAY</h2>
              <div className="dp-birthday-card" ref={pickerContainerRef}>
                <Picker
                  value={pickerValue}
                  onChange={handlePickerChange}
                  wheelMode="normal"
                  itemHeight={30}
                  className="custom-wheel-picker"
                >
                  <Picker.Column name="month">
                    {monthOptions.map((option) => (
                      <Picker.Item key={option.value} value={option.value}>
                        {({ selected }) => (
                          <div
                            className={`picker-text ${selected ? "selected-text" : "unselected-text"}`}
                          >
                            {option.label}
                          </div>
                        )}
                      </Picker.Item>
                    ))}
                  </Picker.Column>

                  <Picker.Column name="day">
                    {dayOptions.map((option) => (
                      <Picker.Item key={option.value} value={option.value}>
                        {({ selected }) => (
                          <div
                            className={`picker-text ${selected ? "selected-text" : "unselected-text"}`}
                          >
                            {option.label}
                          </div>
                        )}
                      </Picker.Item>
                    ))}
                  </Picker.Column>

                  <Picker.Column name="year">
                    {yearOptions.map((option) => (
                      <Picker.Item key={option.value} value={option.value}>
                        {({ selected }) => (
                          <div
                            className={`picker-text ${selected ? "selected-text" : "unselected-text"}`}
                          >
                            {option.label}
                          </div>
                        )}
                      </Picker.Item>
                    ))}
                  </Picker.Column>
                </Picker>
              </div>
              {activeSection === "birthday" && (
                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <h3
                    style={{
                      color: "#47D7AC",
                      fontSize: "18px",
                      fontWeight: "700",
                    }}
                  >
                    {ageText}
                  </h3>
                </div>
              )}
            </div>
          </div>

          <div className="dp-divider-horizontal">
            <span>OR</span>
          </div>

          {/* SECTION 2: AGE GROUP */}
          <div
            className="dp-selection-row"
            style={activeSection === "birthday" ? inactiveStyle : activeStyle}
          >
            <div className="dp-content-column" style={{ width: "100%" }}>
              <h2 className="dp-section-title">AGE GROUP</h2>
              <div className="dp-age-group-list">
                {[
                  { label: "PUPPY", sub: "UP TO 1 YEAR" },
                  { label: "YOUNG DOG / ADOLESCENT", sub: "1 TO 2 YEARS" },
                  { label: "ADULT DOG", sub: "2 TO 7 YEARS" },
                  { label: "SENIOR DOG", sub: "7+ YEARS" },
                ].map((item, index) => (
                  <button
                    key={index}
                    className={`dp-age-btn ${formData.dogAgeGroup === item.label ? "active" : ""}`}
                    onClick={() => handleAgeGroupSelect(item.label)}
                  >
                    <div className="dp-btn-content">
                      {formData.dogAgeGroup === item.label && (
                        <Check
                          size={20}
                          strokeWidth={3}
                          className="dp-check-icon"
                        />
                      )}
                      <div className="dp-text-col">
                        <strong>{item.label}</strong>
                        <small>{item.sub}</small>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DesktopBottomBar
            onNext={handleDesktopNext}
            isNextEnabled={isNextEnabled}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => navigate("/team-paws-personality")}
            variant="green"
          />

          <MobileBottomBar
            onNext={() => setMobileStep(1)}
            isNextEnabled={isRightPanelValid}
            nextIconSrc={nextIcon}
            showSkip={false}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default DogPersonalityPage;
